import React, { useState, useEffect } from 'react';
import {
    Container,
    Paper,
    Typography,
    Box,
    Card,
    CardContent,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Chip,
    Alert,
    CircularProgress,
    Divider,
    IconButton,
    Tooltip,
    TextField,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Snackbar,
    FormControlLabel,
    Checkbox,
    Switch,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    Email as EmailIcon,
    Code as CodeIcon,
    Subject as SubjectIcon,
    Visibility as VisibilityIcon,
    ContentCopy as CopyIcon,
    Edit as EditIcon,
    Save as SaveIcon,
    Cancel as CancelIcon,
    Send as SendIcon
} from '@mui/icons-material';
import { collection, getDocs, doc, getDoc, updateDoc } from 'firebase/firestore';
import { db, fetchEmailTemplates, updateEmailTemplate, sendEmailTemplate, firebaseConfig } from '../firebase';
import { apiFetch, useAuth } from '../hooks/useAuth';

const EmailTemplates = () => {
    const { user, claims } = useAuth();
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedTemplate, setExpandedTemplate] = useState(null);
    const [editingTemplate, setEditingTemplate] = useState(null);
    const [editForm, setEditForm] = useState({ subject: '', html: '', text: '' });
    const [saving, setSaving] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [testEmailDialog, setTestEmailDialog] = useState(null);
    const [testEmail, setTestEmail] = useState('');
    const [sendingTest, setSendingTest] = useState(false);
    const [recipientConfig, setRecipientConfig] = useState({});
    const [savingRecipients, setSavingRecipients] = useState(false);
    const [sentEmails, setSentEmails] = useState([]);
    const [loadingSentEmails, setLoadingSentEmails] = useState(false);
    const [expandedSentEmail, setExpandedSentEmail] = useState(null);
    
    // Global debug BCC configuration
    const [globalDebugBccEnabled, setGlobalDebugBccEnabled] = useState(false);
    const [globalDebugBccEmail, setGlobalDebugBccEmail] = useState('');
    const [savingGlobalDebug, setSavingGlobalDebug] = useState(false);

    // Check if user is authorized (SuperUser/SuperAdmin)
    const isSuperUser = claims?.role === "SuperAdmin";

    useEffect(() => {
        if (isSuperUser) {
            fetchTemplates();
            fetchSentEmails();
            fetchGlobalDebugBcc();
        }
    }, [isSuperUser]);

    const fetchTemplates = async () => {
        try {
            setLoading(true);
            setError(null);
            
            const templatesData = await fetchEmailTemplates();
            setTemplates(templatesData);
            
            // Initialize recipient configuration for each template
            const initialRecipientConfig = {};
            templatesData.forEach(template => {
                initialRecipientConfig[template.id] = {
                    sendToUser: template.sendToUser !== false, // Default to true if not set
                    sendToAdmin: template.sendToAdmin !== false, // Default to true if not set
                    adminEmailOverride: template.adminEmailOverride || '',
                    useDefaultAdminEmail: !template.adminEmailOverride
                };
            });
            setRecipientConfig(initialRecipientConfig);
        } catch (err) {
            console.error('Error fetching templates:', err);
            setError('Fehler beim Laden der E-Mail-Vorlagen');
        } finally {
            setLoading(false);
        }
    };

    const fetchSentEmails = async () => {
        try {
            setLoadingSentEmails(true);
            
            console.log('Fetching sent emails from backend...');
            
            const response = await apiFetch('/app/email-logs');
            
            if (!response.ok) {
                throw new Error('Failed to fetch email logs');
            }
            
            const data = await response.json();
            console.log('Received emails:', data.emails);
            
            // Convert ISO date strings to Date objects for display
            const emails = data.emails.map(email => ({
                ...email,
                createdAt: email.createdAt ? new Date(email.createdAt) : null,
                deliveredAt: email.deliveredAt ? new Date(email.deliveredAt) : null
            }));
            
            setSentEmails(emails);
        } catch (err) {
            console.error('Error fetching sent emails:', err);
            console.error('Error details:', err.message, err.stack);
            // Don't set error state, just log it
        } finally {
            setLoadingSentEmails(false);
        }
    };

    const fetchGlobalDebugBcc = () => {
        try {
            const saved = localStorage.getItem('globalDebugBcc');
            if (saved) {
                const data = JSON.parse(saved);
                setGlobalDebugBccEnabled(data.enabled || false);
                setGlobalDebugBccEmail(data.email || '');
            }
        } catch (err) {
            console.error('Error loading global debug BCC config from localStorage:', err);
            // Don't set error state, just log it
        }
    };

    const handleSaveGlobalDebugBcc = () => {
        try {
            setSavingGlobalDebug(true);
            
            // Validate email if debug BCC is enabled
            if (globalDebugBccEnabled && globalDebugBccEmail && !isValidEmail(globalDebugBccEmail)) {
                setSnackbar({ open: true, message: 'Bitte geben Sie eine gültige Debug-BCC-E-Mail-Adresse ein', severity: 'error' });
                setSavingGlobalDebug(false);
                return;
            }
            
            // Save to localStorage
            const config = {
                enabled: globalDebugBccEnabled,
                email: globalDebugBccEnabled ? globalDebugBccEmail : ''
            };
            localStorage.setItem('globalDebugBcc', JSON.stringify(config));
            
            setSnackbar({ open: true, message: 'Globale Debug-BCC-Konfiguration lokal gespeichert', severity: 'success' });
        } catch (err) {
            console.error('Error saving global debug BCC config to localStorage:', err);
            setSnackbar({ open: true, message: 'Fehler beim Speichern der Debug-BCC-Konfiguration', severity: 'error' });
        } finally {
            setSavingGlobalDebug(false);
        }
    };

    const handleExpandTemplate = (templateId) => {
        setExpandedTemplate(expandedTemplate === templateId ? null : templateId);
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text).then(() => {
            setSnackbar({ open: true, message: 'In Zwischenablage kopiert', severity: 'success' });
        });
    };

    const handleEditTemplate = (template) => {
        setEditingTemplate(template);
        setEditForm({
            subject: template.subject || '',
            html: template.html || '',
            text: template.text || ''
        });
    };

    const handleCancelEdit = () => {
        setEditingTemplate(null);
        setEditForm({ subject: '', html: '', text: '' });
    };

    const handleSaveTemplate = async () => {
        if (!editingTemplate) return;

        try {
            setSaving(true);
            await updateEmailTemplate(editingTemplate.id, editForm);
            
            // Update local state
            setTemplates(templates.map(template => 
                template.id === editingTemplate.id 
                    ? { ...template, ...editForm }
                    : template
            ));
            
            setSnackbar({ open: true, message: 'Vorlage erfolgreich gespeichert', severity: 'success' });
            handleCancelEdit();
        } catch (err) {
            console.error('Error saving template:', err);
            setSnackbar({ open: true, message: 'Fehler beim Speichern der Vorlage', severity: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const handleFormChange = (field, value) => {
        setEditForm(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleRecipientConfigChange = (templateId, field, value) => {
        setRecipientConfig(prev => ({
            ...prev,
            [templateId]: {
                ...prev[templateId],
                [field]: value
            }
        }));
    };

    const isValidEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleSaveRecipientConfig = async (templateId) => {
        try {
            setSavingRecipients(true);
            const config = recipientConfig[templateId];
            
            // Validate email if custom admin email is provided
            if (!config.useDefaultAdminEmail && config.adminEmailOverride && !isValidEmail(config.adminEmailOverride)) {
                setSnackbar({ open: true, message: 'Bitte geben Sie eine gültige E-Mail-Adresse ein', severity: 'error' });
                return;
            }
            
            await updateEmailTemplate(templateId, {
                sendToUser: config.sendToUser,
                sendToAdmin: config.sendToAdmin,
                adminEmailOverride: config.useDefaultAdminEmail ? '' : config.adminEmailOverride
            });
            
            // Update local state
            setTemplates(templates.map(template => 
                template.id === templateId 
                    ? { 
                        ...template, 
                        sendToUser: config.sendToUser,
                        sendToAdmin: config.sendToAdmin,
                        adminEmailOverride: config.useDefaultAdminEmail ? '' : config.adminEmailOverride
                    }
                    : template
            ));
            
            setSnackbar({ open: true, message: 'Empfänger-Konfiguration gespeichert', severity: 'success' });
        } catch (err) {
            console.error('Error saving recipient config:', err);
            setSnackbar({ open: true, message: 'Fehler beim Speichern der Empfänger-Konfiguration', severity: 'error' });
        } finally {
            setSavingRecipients(false);
        }
    };

    const handleTestEmail = (template) => {
        setTestEmailDialog(template);
        setTestEmail('');
    };

    const handleCancelTestEmail = () => {
        setTestEmailDialog(null);
        setTestEmail('');
    };

    const handleSendTestEmail = async () => {
        if (!testEmail || !testEmailDialog) return;

        try {
            setSendingTest(true);
            
            // Create test data for the email template
            const testData = {
                template: testEmailDialog.id,
                user: {
                    name: 'Test User',
                    email: testEmail,
                    cny: 'Test Company'
                },
                form: {
                    accountID: 'TEST123',
                    tenant: 'test',
                    registrationDate: new Date().toLocaleDateString('de-DE'),
                    registrationTime: new Date().toLocaleTimeString('de-DE'),
                    orderDate: new Date().toLocaleDateString('de-DE'),
                    orderTime: new Date().toLocaleTimeString('de-DE'),
                    message: 'This is a test message for the email template.',
                    messageOrder: 'Test order message',
                    messageMagazine: 'Test magazine message',
                    author: 'Test Author',
                    title: 'Test Book Title',
                    ISBN: '978-3-123456-78-9',
                    publisher: 'Test Publisher',
                    costCenter: 'TEST001',
                    deliveryName: 'Test Delivery Name',
                    orderNumber: 'TEST-ORDER-001',
                    invoiceTotal: '29.99',
                    totalItems: '1'
                },
                cart: {
                    '1': {
                        title: 'Test Book',
                        isbn: '978-3-123456-78-9',
                        qty: '1',
                        price: '29.99',
                        nettoPrice: '25.21',
                        tax: '19'
                    }
                },
                currentYear: new Date().getFullYear()
            };

            // Send the test email using the existing sendEmailTemplate function
            await sendEmailTemplate(testData);
            
            setSnackbar({ 
                open: true, 
                message: `Test-E-Mail erfolgreich an ${testEmail} gesendet`, 
                severity: 'success' 
            });
            handleCancelTestEmail();
        } catch (err) {
            console.error('Error sending test email:', err);
            setSnackbar({ 
                open: true, 
                message: 'Fehler beim Senden der Test-E-Mail', 
                severity: 'error' 
            });
        } finally {
            setSendingTest(false);
        }
    };

    const getTemplateTypeColor = (templateName) => {
        if (templateName.includes('order')) return 'primary';
        if (templateName.includes('register') || templateName.includes('activation')) return 'success';
        if (templateName.includes('contact')) return 'info';
        if (templateName.includes('debug')) return 'warning';
        if (templateName.includes('edi')) return 'secondary';
        return 'default';
    };

    const getTemplateTypeLabel = (templateName) => {
        if (templateName.includes('order')) return 'Bestellung';
        if (templateName.includes('register')) return 'Registrierung';
        if (templateName.includes('activation')) return 'Aktivierung';
        if (templateName.includes('contact')) return 'Kontakt';
        if (templateName.includes('debug')) return 'Debug';
        if (templateName.includes('edi')) return 'EDI';
        return 'Allgemein';
    };

    // Check authorization first
    if (!isSuperUser) {
        return (
            <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
                <Alert severity="error">
                    Sie haben keine Berechtigung, diese Seite anzuzeigen. Nur SuperUser haben Zugriff auf E-Mail-Vorlagen.
                </Alert>
            </Container>
        );
    }

    if (loading) {
        return (
            <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
                <Alert severity="error">{error}</Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Paper elevation={3} sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" mb={3}>
                    <EmailIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
                    <Typography variant="h4" component="h1">
                        E-Mail-Vorlagen
                    </Typography>
                </Box>
                
                <Typography variant="body1" color="text.secondary" mb={3}>
                    Hier können Sie alle verfügbaren E-Mail-Vorlagen einsehen, die im System verwendet werden.
                </Typography>

                {/* Admin Email Configuration Display */}
                <Paper variant="outlined" sx={{ p: 2, mb: 3, backgroundColor: 'grey.50' }}>
                    <Typography variant="h6" gutterBottom display="flex" alignItems="center">
                        <EmailIcon sx={{ mr: 1 }} />
                        Standard-E-Mail-Konfiguration
                    </Typography>
                    <Box display="flex" flexDirection="column" gap={1}>
                        <Box display="flex" alignItems="center">
                            <Typography variant="body2" color="text.secondary" sx={{ minWidth: '120px' }}>
                                Standard-Admin:
                            </Typography>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'medium' }}>
                                {firebaseConfig.email ? (Array.isArray(firebaseConfig.email) ? firebaseConfig.email.join(', ') : (typeof firebaseConfig.email === 'string' ? firebaseConfig.email.split(',').map(email => email.trim()).join(', ') : firebaseConfig.email)) : 'Nicht konfiguriert'}
                            </Typography>
                        </Box>
                        {firebaseConfig.emailOrders && (
                            <Box display="flex" alignItems="center">
                                <Typography variant="body2" color="text.secondary" sx={{ minWidth: '120px' }}>
                                    Bestellungen:
                                </Typography>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'medium' }}>
                                    {Array.isArray(firebaseConfig.emailOrders) ? firebaseConfig.emailOrders.join(', ') : (typeof firebaseConfig.emailOrders === 'string' ? firebaseConfig.emailOrders.split(',').map(email => email.trim()).join(', ') : firebaseConfig.emailOrders)}
                                </Typography>
                            </Box>
                        )}
                        {firebaseConfig.emailAbos && (
                            <Box display="flex" alignItems="center">
                                <Typography variant="body2" color="text.secondary" sx={{ minWidth: '120px' }}>
                                    Abos:
                                </Typography>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'medium' }}>
                                    {Array.isArray(firebaseConfig.emailAbos) ? firebaseConfig.emailAbos.join(', ') : (typeof firebaseConfig.emailAbos === 'string' ? firebaseConfig.emailAbos.split(',').map(email => email.trim()).join(', ') : firebaseConfig.emailAbos)}
                                </Typography>
                            </Box>
                        )}
                        {firebaseConfig.emailEdi && (
                            <Box display="flex" alignItems="center">
                                <Typography variant="body2" color="text.secondary" sx={{ minWidth: '120px' }}>
                                    EDI:
                                </Typography>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'medium' }}>
                                    {Array.isArray(firebaseConfig.emailEdi) ? firebaseConfig.emailEdi.join(', ') : (typeof firebaseConfig.emailEdi === 'string' ? firebaseConfig.emailEdi.split(',').map(email => email.trim()).join(', ') : firebaseConfig.emailEdi)}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Paper>

                {/* Global Debug BCC Configuration */}
                <Paper variant="outlined" sx={{ p: 3, mb: 3, backgroundColor: 'warning.50', borderColor: 'warning.main' }}>
                    <Typography variant="h6" gutterBottom display="flex" alignItems="center" color="warning.dark">
                        <EmailIcon sx={{ mr: 1 }} />
                        Globale Debug-BCC-Konfiguration
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                        Diese BCC-Adresse wird bei allen versendeten E-Mails hinzugefügt, wenn aktiviert.
                    </Typography>
                    
                    <Box display="flex" flexDirection="column" gap={2}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={globalDebugBccEnabled}
                                    onChange={(e) => setGlobalDebugBccEnabled(e.target.checked)}
                                    color="warning"
                                />
                            }
                            label="Debug-BCC global aktivieren"
                        />
                        
                        {globalDebugBccEnabled && (
                            <TextField
                                fullWidth
                                size="small"
                                label="Debug-BCC-E-Mail-Adresse"
                                placeholder="debug@example.com"
                                value={globalDebugBccEmail}
                                onChange={(e) => setGlobalDebugBccEmail(e.target.value)}
                                helperText="Diese Adresse erhält eine BCC-Kopie aller E-Mails"
                                error={globalDebugBccEmail && !isValidEmail(globalDebugBccEmail)}
                                type="email"
                                sx={{ maxWidth: 500 }}
                            />
                        )}
                        
                        <Box display="flex" justifyContent="flex-end">
                            <Button
                                variant="contained"
                                color="warning"
                                size="small"
                                onClick={handleSaveGlobalDebugBcc}
                                disabled={savingGlobalDebug}
                                startIcon={savingGlobalDebug ? <CircularProgress size={16} /> : <SaveIcon />}
                            >
                                {savingGlobalDebug ? 'Speichern...' : 'Debug-BCC speichern'}
                            </Button>
                        </Box>
                    </Box>
                </Paper>

                {templates.length === 0 ? (
                    <Alert severity="info">
                        Keine E-Mail-Vorlagen gefunden.
                    </Alert>
                ) : (
                    <Box>
                        {templates.map((template) => (
                            <Card key={template.id} sx={{ mb: 2 }}>
                                <Accordion 
                                    expanded={expandedTemplate === template.id}
                                    onChange={() => handleExpandTemplate(template.id)}
                                >
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon />}
                                        sx={{ 
                                            backgroundColor: expandedTemplate === template.id ? 'primary.50' : 'transparent',
                                            '&:hover': { backgroundColor: 'primary.25' }
                                        }}
                                    >
                                        <Box display="flex" alignItems="center" width="100%">
                                            <Box flexGrow={1}>
                                                <Box display="flex" alignItems="center" mb={1}>
                                                    <Typography variant="h6" component="div">
                                                        {template.id}
                                                    </Typography>
                                                    <Chip 
                                                        label={getTemplateTypeLabel(template.id)}
                                                        color={getTemplateTypeColor(template.id)}
                                                        size="small"
                                                        sx={{ ml: 2 }}
                                                    />
                                                </Box>
                                                <Box display="flex" alignItems="center" color="text.secondary">
                                                    <SubjectIcon sx={{ mr: 1, fontSize: 16 }} />
                                                    <Typography variant="body2" noWrap>
                                                        {template.subject || 'Kein Betreff'}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Box>
                                    </AccordionSummary>
                                    
                                    <AccordionDetails>
                                        <Box>
                                            {/* Subject Section */}
                                            <Box mb={3}>
                                                <Typography variant="h6" gutterBottom display="flex" alignItems="center">
                                                    <SubjectIcon sx={{ mr: 1 }} />
                                                    Betreff
                                                </Typography>
                                                <Paper 
                                                    variant="outlined" 
                                                    sx={{ p: 2, backgroundColor: 'grey.50' }}
                                                >
                                                    <Typography variant="body1" fontFamily="monospace">
                                                        {template.subject || 'Kein Betreff definiert'}
                                                    </Typography>
                                                </Paper>
                                            </Box>

                                            <Divider sx={{ mb: 3 }} />

                                            {/* HTML Content Section - Side by Side */}
                                            <Box mb={3}>
                                                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                                                    <Typography variant="h6" display="flex" alignItems="center">
                                                        <CodeIcon sx={{ mr: 1 }} />
                                                        HTML-Inhalt & Vorschau
                                                    </Typography>
                                                    <Box>
                                                        <Tooltip title="Test-E-Mail senden">
                                                            <IconButton 
                                                                size="small"
                                                                onClick={() => handleTestEmail(template)}
                                                            >
                                                                <SendIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Vorlage bearbeiten">
                                                            <IconButton 
                                                                size="small"
                                                                onClick={() => handleEditTemplate(template)}
                                                            >
                                                                <EditIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="HTML-Vorschau in neuem Fenster">
                                                            <IconButton 
                                                                size="small"
                                                                onClick={() => {
                                                                    const newWindow = window.open('', '_blank');
                                                                    newWindow.document.write(template.html || '');
                                                                    newWindow.document.close();
                                                                }}
                                                            >
                                                                <VisibilityIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="HTML kopieren">
                                                            <IconButton 
                                                                size="small"
                                                                onClick={() => copyToClipboard(template.html || '')}
                                                            >
                                                                <CopyIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                </Box>
                                                
                                                {/* Side by Side Layout */}
                                                <Box display="flex" gap={2} sx={{ flexDirection: { xs: 'column', lg: 'row' } }}>
                                                    {/* HTML Source Code */}
                                                    <Box flex={1}>
                                                        <Typography variant="subtitle2" gutterBottom color="text.secondary">
                                                            HTML-Quellcode
                                                        </Typography>
                                                        <Paper 
                                                            variant="outlined" 
                                                            sx={{ 
                                                                p: 2, 
                                                                backgroundColor: 'grey.50',
                                                                height: '400px',
                                                                overflow: 'auto'
                                                            }}
                                                        >
                                                            <Typography 
                                                                variant="body2" 
                                                                component="pre" 
                                                                sx={{ 
                                                                    fontFamily: 'monospace',
                                                                    whiteSpace: 'pre-wrap',
                                                                    wordBreak: 'break-word',
                                                                    margin: 0,
                                                                    fontSize: '0.875rem'
                                                                }}
                                                            >
                                                                {template.html || 'Kein HTML-Inhalt definiert'}
                                                            </Typography>
                                                        </Paper>
                                                    </Box>

                                                    {/* Rendered Preview */}
                                                    <Box flex={1}>
                                                        <Typography variant="subtitle2" gutterBottom color="text.secondary">
                                                            Vorschau
                                                        </Typography>
                                                        <Paper 
                                                            variant="outlined" 
                                                            sx={{ 
                                                                p: 2, 
                                                                backgroundColor: 'white',
                                                                height: '400px',
                                                                overflow: 'auto',
                                                                border: '1px solid #e0e0e0'
                                                            }}
                                                        >
                                                            <Box 
                                                                dangerouslySetInnerHTML={{ 
                                                                    __html: template.html || '<p>Kein HTML-Inhalt definiert</p>' 
                                                                }}
                                                                sx={{
                                                                    '& h1, & h2, & h3, & h4, & h5, & h6': {
                                                                        marginTop: 0,
                                                                        marginBottom: '0.5em'
                                                                    },
                                                                    '& p': {
                                                                        marginBottom: '1em'
                                                                    },
                                                                    '& ul, & ol': {
                                                                        marginBottom: '1em'
                                                                    },
                                                                    '& li': {
                                                                        marginBottom: '0.25em'
                                                                    }
                                                                }}
                                                            />
                                                        </Paper>
                                                    </Box>
                                                </Box>
                                            </Box>

                                            {/* Text Content Section (if available) */}
                                            {template.text && (
                                                <>
                                                    <Divider sx={{ mb: 3 }} />
                                                    <Box mb={3}>
                                                        <Typography variant="h6" gutterBottom>
                                                            Text-Inhalt
                                                        </Typography>
                                                        <Paper 
                                                            variant="outlined" 
                                                            sx={{ 
                                                                p: 2, 
                                                                backgroundColor: 'grey.50',
                                                                maxHeight: '200px',
                                                                overflow: 'auto'
                                                            }}
                                                        >
                                                            <Typography 
                                                                variant="body2" 
                                                                component="pre" 
                                                                sx={{ 
                                                                    fontFamily: 'monospace',
                                                                    whiteSpace: 'pre-wrap',
                                                                    wordBreak: 'break-word',
                                                                    margin: 0
                                                                }}
                                                            >
                                                                {template.text}
                                                            </Typography>
                                                        </Paper>
                                                    </Box>
                                                </>
                                            )}

                                            {/* Recipient Configuration Section */}
                                            <Divider sx={{ mb: 3 }} />
                                            <Box mb={3}>
                                                <Typography variant="h6" gutterBottom display="flex" alignItems="center">
                                                    <EmailIcon sx={{ mr: 1 }} />
                                                    Empfänger-Konfiguration
                                                </Typography>
                                                <Paper variant="outlined" sx={{ p: 3 }}>
                                                    <Box display="flex" flexDirection="column" gap={2}>
                                                        {/* User Email Configuration */}
                                                        <Box>
                                                            <FormControlLabel
                                                                control={
                                                                    <Switch
                                                                        checked={recipientConfig[template.id]?.sendToUser || false}
                                                                        onChange={(e) => handleRecipientConfigChange(template.id, 'sendToUser', e.target.checked)}
                                                                        color="primary"
                                                                    />
                                                                }
                                                                label="E-Mail an Benutzer senden"
                                                            />
                                                            <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                                                                Sendet eine Kopie der E-Mail an den angemeldeten Benutzer
                                                            </Typography>
                                                        </Box>

                                                        {/* Admin Email Configuration */}
                                                        <Box>
                                                            <FormControlLabel
                                                                control={
                                                                    <Switch
                                                                        checked={recipientConfig[template.id]?.sendToAdmin || false}
                                                                        onChange={(e) => handleRecipientConfigChange(template.id, 'sendToAdmin', e.target.checked)}
                                                                        color="primary"
                                                                    />
                                                                }
                                                                label="E-Mail an Administrator senden"
                                                            />
                                                            <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                                                                Sendet eine Kopie der E-Mail an den Administrator
                                                            </Typography>
                                                        </Box>

                                                        {/* Admin Email Override */}
                                                        {recipientConfig[template.id]?.sendToAdmin && (
                                                            <Box mt={2}>
                                                                <FormControlLabel
                                                                    control={
                                                                        <Checkbox
                                                                            checked={!recipientConfig[template.id]?.useDefaultAdminEmail}
                                                                            onChange={(e) => handleRecipientConfigChange(template.id, 'useDefaultAdminEmail', !e.target.checked)}
                                                                            color="primary"
                                                                        />
                                                                    }
                                                                    label="Standard-Admin-E-Mail überschreiben"
                                                                />
                                                                
                                                                {!recipientConfig[template.id]?.useDefaultAdminEmail && (
                                                                    <Box mt={1} sx={{ ml: 4 }}>
                                                                        <TextField
                                                                            fullWidth
                                                                            size="small"
                                                                            label="Admin-E-Mail-Adresse"
                                                                            placeholder="admin@example.com"
                                                                            value={recipientConfig[template.id]?.adminEmailOverride || ''}
                                                                            onChange={(e) => handleRecipientConfigChange(template.id, 'adminEmailOverride', e.target.value)}
                                                                            helperText="Überschreibt die Standard-Admin-E-Mail für diese Vorlage"
                                                                            error={recipientConfig[template.id]?.adminEmailOverride && !isValidEmail(recipientConfig[template.id]?.adminEmailOverride)}
                                                                            type="email"
                                                                        />
                                                                    </Box>
                                                                )}
                                                            </Box>
                                                        )}

                                                        {/* Save Button */}
                                                        <Box display="flex" justifyContent="flex-end" mt={2}>
                                                            <Button
                                                                variant="contained"
                                                                color="primary"
                                                                size="small"
                                                                onClick={() => handleSaveRecipientConfig(template.id)}
                                                                disabled={savingRecipients}
                                                                startIcon={savingRecipients ? <CircularProgress size={16} /> : <SaveIcon />}
                                                            >
                                                                {savingRecipients ? 'Speichern...' : 'Empfänger speichern'}
                                                            </Button>
                                                        </Box>
                                                    </Box>
                                                </Paper>
                                            </Box>
                                        </Box>
                                    </AccordionDetails>
                                </Accordion>
                            </Card>
                        ))}
                    </Box>
                )}

                {/* Sent Emails Section */}
                <Box mt={4}>
                    <Divider sx={{ mb: 3 }} />
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                        <Box display="flex" alignItems="center">
                            <SendIcon sx={{ mr: 2, fontSize: 28, color: 'primary.main' }} />
                            <Typography variant="h5" component="h2">
                                Zuletzt versendete E-Mails
                            </Typography>
                        </Box>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={fetchSentEmails}
                            disabled={loadingSentEmails}
                        >
                            {loadingSentEmails ? 'Laden...' : 'Aktualisieren'}
                        </Button>
                    </Box>

                    {loadingSentEmails ? (
                        <Box display="flex" justifyContent="center" p={3}>
                            <CircularProgress />
                        </Box>
                    ) : sentEmails.length === 0 ? (
                        <Alert severity="info">
                            Keine versendeten E-Mails gefunden
                        </Alert>
                    ) : (
                        <Box>
                            {sentEmails.map((email) => (
                                <Accordion
                                    key={email.id}
                                    expanded={expandedSentEmail === email.id}
                                    onChange={() => setExpandedSentEmail(expandedSentEmail === email.id ? null : email.id)}
                                    sx={{ mb: 1 }}
                                >
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Box display="flex" alignItems="center" width="100%" gap={2}>
                                            <Chip
                                                label={email.template}
                                                color={getTemplateTypeColor(email.template)}
                                                size="small"
                                            />
                                            <Typography variant="body2" sx={{ flexGrow: 1 }}>
                                                An: <strong>{email.to}</strong>
                                            </Typography>
                                            <Chip
                                                label={email.state}
                                                color={email.state === 'SUCCESS' ? 'success' : email.state === 'ERROR' ? 'error' : 'default'}
                                                size="small"
                                            />
                                            {email.deliveredAt && (
                                                <Typography variant="caption" color="text.secondary">
                                                    {email.deliveredAt.toLocaleString('de-DE')}
                                                </Typography>
                                            )}
                                        </Box>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Box>
                                            <Box display="flex" flexDirection="column" gap={1} mb={2}>
                                                <Box display="flex" gap={1}>
                                                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100 }}>
                                                        <strong>Empfänger:</strong>
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                                        {email.to}
                                                    </Typography>
                                                </Box>
                                                {email.from && (
                                                    <Box display="flex" gap={1}>
                                                        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100 }}>
                                                            <strong>Absender:</strong>
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                                            {email.from}
                                                        </Typography>
                                                    </Box>
                                                )}
                                                <Box display="flex" gap={1}>
                                                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100 }}>
                                                        <strong>Vorlage:</strong>
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        {email.template}
                                                    </Typography>
                                                </Box>
                                                <Box display="flex" gap={1}>
                                                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100 }}>
                                                        <strong>Betreff:</strong>
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        {email.subject}
                                                    </Typography>
                                                </Box>
                                                <Box display="flex" gap={1}>
                                                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100 }}>
                                                        <strong>Status:</strong>
                                                    </Typography>
                                                    <Chip
                                                        label={email.state}
                                                        color={email.state === 'SUCCESS' ? 'success' : email.state === 'ERROR' ? 'error' : 'default'}
                                                        size="small"
                                                    />
                                                </Box>
                                                {email.createdAt && (
                                                    <Box display="flex" gap={1}>
                                                        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100 }}>
                                                            <strong>Erstellt:</strong>
                                                        </Typography>
                                                        <Typography variant="body2">
                                                            {email.createdAt.toLocaleString('de-DE')}
                                                        </Typography>
                                                    </Box>
                                                )}
                                                {email.deliveredAt && (
                                                    <Box display="flex" gap={1}>
                                                        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100 }}>
                                                            <strong>Zugestellt:</strong>
                                                        </Typography>
                                                        <Typography variant="body2">
                                                            {email.deliveredAt.toLocaleString('de-DE')}
                                                        </Typography>
                                                    </Box>
                                                )}
                                                {email.messageId && (
                                                    <Box display="flex" gap={1}>
                                                        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100 }}>
                                                            <strong>Message-ID:</strong>
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '11px' }}>
                                                            {email.messageId}
                                                        </Typography>
                                                    </Box>
                                                )}
                                                {email.accepted && email.accepted.length > 0 && (
                                                    <Box display="flex" gap={1}>
                                                        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100 }}>
                                                            <strong>Akzeptiert:</strong>
                                                        </Typography>
                                                        <Typography variant="body2" color="success.main">
                                                            {email.accepted.join(', ')}
                                                        </Typography>
                                                    </Box>
                                                )}
                                                {email.rejected && email.rejected.length > 0 && (
                                                    <Box display="flex" gap={1}>
                                                        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100 }}>
                                                            <strong>Abgelehnt:</strong>
                                                        </Typography>
                                                        <Typography variant="body2" color="error">
                                                            {email.rejected.join(', ')}
                                                        </Typography>
                                                    </Box>
                                                )}
                                                {email.error && (
                                                    <Box display="flex" gap={1}>
                                                        <Typography variant="body2" color="error" sx={{ minWidth: 100 }}>
                                                            <strong>Fehler:</strong>
                                                        </Typography>
                                                        <Typography variant="body2" color="error">
                                                            {email.error}
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </Box>

                                            {email.data && (
                                                <Box mt={2}>
                                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                                        <strong>E-Mail-Daten:</strong>
                                                    </Typography>
                                                    <Paper
                                                        variant="outlined"
                                                        sx={{
                                                            p: 2,
                                                            backgroundColor: 'grey.50',
                                                            maxHeight: 300,
                                                            overflow: 'auto'
                                                        }}
                                                    >
                                                        <pre style={{ margin: 0, fontSize: '12px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                            {JSON.stringify(email.data, null, 2)}
                                                        </pre>
                                                    </Paper>
                                                </Box>
                                            )}
                                        </Box>
                                    </AccordionDetails>
                                </Accordion>
                            ))}
                        </Box>
                    )}
                </Box>
            </Paper>

            {/* Edit Dialog */}
            <Dialog 
                open={!!editingTemplate} 
                onClose={handleCancelEdit}
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" alignItems="center">
                        <EditIcon sx={{ mr: 1 }} />
                        Vorlage bearbeiten: {editingTemplate?.id}
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2 }}>
                        {/* Subject Field */}
                        <TextField
                            fullWidth
                            label="Betreff"
                            value={editForm.subject}
                            onChange={(e) => handleFormChange('subject', e.target.value)}
                            margin="normal"
                            variant="outlined"
                        />

                        {/* HTML Content Field */}
                        <TextField
                            fullWidth
                            label="HTML-Inhalt"
                            value={editForm.html}
                            onChange={(e) => handleFormChange('html', e.target.value)}
                            margin="normal"
                            variant="outlined"
                            multiline
                            rows={12}
                            sx={{ fontFamily: 'monospace' }}
                        />

                        {/* Text Content Field */}
                        <TextField
                            fullWidth
                            label="Text-Inhalt (optional)"
                            value={editForm.text}
                            onChange={(e) => handleFormChange('text', e.target.value)}
                            margin="normal"
                            variant="outlined"
                            multiline
                            rows={6}
                            sx={{ fontFamily: 'monospace' }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={handleCancelEdit}
                        startIcon={<CancelIcon />}
                        disabled={saving}
                    >
                        Abbrechen
                    </Button>
                    <Button 
                        onClick={handleSaveTemplate}
                        variant="contained"
                        startIcon={<SaveIcon />}
                        disabled={saving}
                    >
                        {saving ? 'Speichern...' : 'Speichern'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Test Email Dialog */}
            <Dialog 
                open={!!testEmailDialog} 
                onClose={handleCancelTestEmail}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" alignItems="center">
                        <SendIcon sx={{ mr: 1 }} />
                        Test-E-Mail senden: {testEmailDialog?.id}
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2 }}>
                        <Typography variant="body2" color="text.secondary" mb={2}>
                            Geben Sie eine E-Mail-Adresse ein, an die die Test-E-Mail gesendet werden soll.
                        </Typography>
                        <TextField
                            fullWidth
                            label="E-Mail-Adresse"
                            type="email"
                            value={testEmail}
                            onChange={(e) => setTestEmail(e.target.value)}
                            margin="normal"
                            variant="outlined"
                            placeholder="test@example.com"
                        />
                        <Alert severity="info" sx={{ mt: 2 }}>
                            <Typography variant="body2">
                                Die Test-E-Mail wird mit Beispieldaten gesendet, um die Vorlage zu testen.
                            </Typography>
                        </Alert>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={handleCancelTestEmail}
                        disabled={sendingTest}
                    >
                        Abbrechen
                    </Button>
                    <Button 
                        onClick={handleSendTestEmail}
                        variant="contained"
                        startIcon={<SendIcon />}
                        disabled={sendingTest || !testEmail}
                    >
                        {sendingTest ? 'Senden...' : 'Test-E-Mail senden'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert 
                    onClose={() => setSnackbar({ ...snackbar, open: false })} 
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default EmailTemplates;
